import { useTranslation } from 'react-i18next';

import { useUpdateWorkspace } from '@/entities/collab';
import { usePaletteOptions } from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { useErrorMessage } from '@/shared/i18n';
import { Icons, PalettePicker, Popover } from '@/shared/ui';

import styles from './WorkspaceColor.module.css';

/**
 * The header-color control of a workspace: a palette icon that opens the fixed palette. The color
 * only tints this workspace's header - never its boards or databases. Offered to those who may
 * change the workspace settings; the server decides.
 */
export function WorkspaceColor({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const update = useUpdateWorkspace(workspace.id);
  const message = useErrorMessage();
  const options = usePaletteOptions();
  return (
    <Popover
      label={t('catalog.workspaceColorTitle')}
      width={280}
      alignEnd
      trigger={(props) => (
        <button
          type="button"
          {...props}
          className={styles.trigger}
          aria-label={t('catalog.workspaceColor')}
          title={t('catalog.workspaceColor')}
        >
          <Icons.palette />
        </button>
      )}
    >
      {(close) => (
        <div className={styles.panel}>
          <PalettePicker
            label={t('catalog.workspaceColor')}
            value={workspace.color}
            options={options}
            noneLabel={t('databases.palette.none')}
            custom
            workspaceId={workspace.id}
            disabled={update.isPending}
            onPick={(color) => {
              update.mutate({ color }, { onSuccess: close });
            }}
          />
          <p className={styles.hint}>{t('catalog.workspaceColorHint')}</p>
          {update.error && (
            <p role="alert" className={styles.hint}>
              {message(update.error)}
            </p>
          )}
        </div>
      )}
    </Popover>
  );
}
